import { IVatCheckStatus, VatCheckState } from "../types/vatTypes";

// EU member states plus Northern Ireland, keyed by ISO alpha-2 with the prefix
// VIES uses. Mirrors the backend's `EuVatCountryCodes` (GR is EL in VIES).
const ISO_TO_VIES_PREFIX: Record<string, string> = {
  AT: "AT",
  BE: "BE",
  BG: "BG",
  CY: "CY",
  CZ: "CZ",
  DE: "DE",
  DK: "DK",
  EE: "EE",
  GR: "EL",
  ES: "ES",
  FI: "FI",
  FR: "FR",
  HR: "HR",
  HU: "HU",
  IE: "IE",
  IT: "IT",
  LT: "LT",
  LU: "LU",
  LV: "LV",
  MT: "MT",
  NL: "NL",
  PL: "PL",
  PT: "PT",
  RO: "RO",
  SE: "SE",
  SI: "SI",
  SK: "SK",
  XI: "XI"
};

export const EU_VAT_COUNTRIES: readonly string[] =
  Object.keys(ISO_TO_VIES_PREFIX);

const KNOWN_VAT_PREFIXES = new Set([
  ...Object.keys(ISO_TO_VIES_PREFIX),
  ...Object.values(ISO_TO_VIES_PREFIX)
]);

// Number part of the VAT per VIES prefix, moved from the activation form's
// `vatPatterns` (EU + XI only; XI uses the UK pattern). No checksums.
const VAT_BODY_PATTERNS: Record<string, RegExp> = {
  AT: /^U\d{8}$/,
  BE: /^\d{10}$/,
  BG: /^\d{9,10}$/,
  CY: /^[0-59]\d{7}[A-Z]$/,
  CZ: /^\d{8,10}$/,
  DE: /^[1-9]\d{8}$/,
  DK: /^\d{8}$/,
  EE: /^10\d{7}$/,
  EL: /^\d{9}$/,
  ES: /^[0-9A-Z]\d{7}[0-9A-Z]$/,
  FI: /^\d{8}$/,
  FR: /^[0-9A-Z]{2}\d{9}$/,
  HR: /^\d{11}$/,
  HU: /^\d{8}$/,
  IE: /^[0-9A-Z*+]{7}[A-Z]{1,2}$/,
  IT: /^\d{11}$/,
  LT: /^(\d{9}|\d{12})$/,
  LU: /^\d{8}$/,
  LV: /^\d{11}$/,
  MT: /^[1-9]\d{7}$/,
  NL: /^\d{9}B\d{2}$/,
  PL: /^\d{10}$/,
  PT: /^\d{9}$/,
  RO: /^[1-9]\d{1,9}$/,
  SE: /^\d{10}01$/,
  SI: /^[1-9]\d{7}$/,
  SK: /^[1-9]\d([2-4]|[6-9])\d{7}$/,
  XI: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/
};

export const VAT_MESSAGE_KEYS = {
  required: "vatValidation.required",
  malformed: "vatValidation.malformed",
  mismatch: "vatValidation.mismatch",
  checking: "vatValidation.checking",
  valid: "vatValidation.valid",
  invalid: "vatValidation.invalid",
  unavailable: "vatValidation.unavailable",
  unsupported: "vatValidation.unsupported",
  saveNotConfirmed: "vatValidation.saveNotConfirmed"
} as const;

const SAVE_BLOCKING_STATES: ReadonlySet<VatCheckState> = new Set<VatCheckState>(
  ["pending", "checking", "invalid", "unavailable", "malformed", "mismatch"]
);

export const normalizeCountryCode = (countryCode?: string | null) =>
  countryCode?.trim().toUpperCase() || null;

export const isEuVatCountry = (countryCode?: string | null) => {
  const country = normalizeCountryCode(countryCode);
  return country !== null && country in ISO_TO_VIES_PREFIX;
};

export const toViesPrefix = (countryCode?: string | null) => {
  const country = normalizeCountryCode(countryCode);
  return country ? (ISO_TO_VIES_PREFIX[country] ?? null) : null;
};

export const normalizeVat = (value?: string | null) =>
  (value ?? "").replace(/\s+/g, "").toUpperCase();

export const hasAllowedVatCharacters = (value?: string | null) =>
  /^[A-Z0-9]*$/.test(normalizeVat(value));

// Mirrors the backend's `VatNumberNormalizer.TryStripMatchingPrefix`: only a
// known EU/XI prefix is compared with the country, and a matching one is
// stripped from the body.
export const splitPrefix = (
  countryCode: string | null | undefined,
  value: string | null | undefined
): { ok: boolean; prefix: string | null; body: string } => {
  const normalized = normalizeVat(value);
  const prefix = normalized.slice(0, 2);

  if (!/^[A-Z]{2}$/.test(prefix) || !KNOWN_VAT_PREFIXES.has(prefix)) {
    return { ok: true, prefix: null, body: normalized };
  }

  const country = normalizeCountryCode(countryCode);
  const matches = prefix === country || prefix === toViesPrefix(country);

  return matches
    ? { ok: true, prefix, body: normalized.slice(2) }
    : { ok: false, prefix, body: normalized };
};

export const hasMismatchedPrefix = (
  countryCode: string | null | undefined,
  value: string | null | undefined
) => !splitPrefix(countryCode, value).ok;

// Countries outside the EU are not checked; the endpoint answers `unsupported`.
export const matchesCountryFormat = (
  countryCode: string | null | undefined,
  value: string | null | undefined
) => {
  const viesPrefix = toViesPrefix(countryCode);
  if (!viesPrefix) return true;

  const { ok, body } = splitPrefix(countryCode, value);
  return ok && VAT_BODY_PATTERNS[viesPrefix].test(body);
};

export const getLocalVatError = (
  countryCode: string | null | undefined,
  value: string | null | undefined
): "malformed" | "mismatch" | null => {
  if (!normalizeVat(value)) return null;
  if (!hasAllowedVatCharacters(value)) return "malformed";
  if (!normalizeCountryCode(countryCode)) return null;
  if (hasMismatchedPrefix(countryCode, value)) return "mismatch";
  if (!matchesCountryFormat(countryCode, value)) return "malformed";
  return null;
};

export const findCountryAlpha2 = (
  countries: Array<{ id: number | string; alpha2Code?: string | null }>,
  countryId: number | string | null | undefined
) => {
  if (countryId === null || countryId === undefined || countryId === "") {
    return null;
  }

  const country = countries.find(({ id }) => String(id) === String(countryId));
  return normalizeCountryCode(country?.alpha2Code);
};

// `customerType` is "Business" or "Private"; when it is empty the backend
// treats an account with a VAT or KVK number as business.
export const isBusinessCustomer = ({
  customerType,
  vatNumber,
  kvkcocNumber
}: {
  customerType?: string | null;
  vatNumber?: string | null;
  kvkcocNumber?: string | null;
}) => {
  const type = customerType?.trim().toLowerCase();
  if (type) return type === "business";

  return Boolean(normalizeVat(vatNumber)) || Boolean(kvkcocNumber?.trim());
};

export const isVatRequired = ({
  countryCode,
  ...customer
}: {
  customerType?: string | null;
  vatNumber?: string | null;
  kvkcocNumber?: string | null;
  countryCode?: string | null;
}) => isBusinessCustomer(customer) && isEuVatCountry(countryCode);

// Mirrors the backend's `IsUnchanged`: the loaded VAT is not validated again
// when the value (without its prefix) and the billing country are the same.
export const isVatUnchanged = ({
  value,
  countryCode,
  initialValue,
  initialCountryCode
}: {
  value?: string | null;
  countryCode?: string | null;
  initialValue?: string | null;
  initialCountryCode?: string | null;
}) => {
  const country = normalizeCountryCode(countryCode);
  if (!normalizeVat(initialValue) || !country) return false;
  if (country !== normalizeCountryCode(initialCountryCode)) return false;

  const current = splitPrefix(country, value);
  const initial = splitPrefix(country, initialValue);
  return current.ok && initial.ok && current.body === initial.body;
};

export const isVatBlockingSave = (
  status: IVatCheckStatus | null | undefined,
  {
    required,
    value,
    countryCode
  }: {
    required: boolean;
    value?: string | null;
    countryCode?: string | null;
  }
) => {
  const normalized = normalizeVat(value);
  if (!normalized) return required;

  const isCurrent =
    status?.checkedValue === normalized &&
    status.checkedCountry === normalizeCountryCode(countryCode);

  return !isCurrent || SAVE_BLOCKING_STATES.has(status.state);
};

// A 400 body is never read: the Customer proxy sends it empty, so the message
// comes from the value that was sent.
export const mapValidateResponse = (
  httpStatus: number | undefined,
  body: unknown,
  { countryCode, vatNumber }: { countryCode: string; vatNumber: string }
): { state: VatCheckState; message: string } => {
  if (httpStatus === 400) {
    return hasMismatchedPrefix(countryCode, vatNumber)
      ? { state: "mismatch", message: VAT_MESSAGE_KEYS.mismatch }
      : { state: "malformed", message: VAT_MESSAGE_KEYS.malformed };
  }

  const status =
    httpStatus === 200
      ? String(
          (body as { status?: unknown } | null)?.status ?? ""
        ).toLowerCase()
      : "";

  switch (status) {
    case "valid":
      return { state: "valid", message: VAT_MESSAGE_KEYS.valid };
    case "invalid":
      return { state: "invalid", message: VAT_MESSAGE_KEYS.invalid };
    case "unsupported":
      return { state: "unsupported", message: VAT_MESSAGE_KEYS.unsupported };
    default:
      return { state: "unavailable", message: VAT_MESSAGE_KEYS.unavailable };
  }
};
